import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ActivityTip } from '@/types/activity';

interface SortableTipItemProps {
  id: string;
  tip: ActivityTip;
  index: number;
  onUpdate: (index: number, field: 'boldText' | 'text', value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

export function SortableTipItem({
  id,
  tip,
  index,
  onUpdate,
  onRemove,
  canRemove,
}: SortableTipItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <div className="flex gap-4 items-start">
        {/* Drag handle */}
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing mt-2 touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-5 h-5 text-admin-slate-500 hover:text-admin-slate-700" />
        </button>

        {/* Tip inputs */}
        <div className="flex-1 space-y-3">
          <div>
            <Label htmlFor={`tip-${index}-text`}>Full Tip Text *</Label>
            <Input
              id={`tip-${index}-text`}
              value={tip.text}
              onChange={(e) => onUpdate(index, 'text', e.target.value)}
              placeholder="Full tip text..."
              maxLength={500}
              required
            />
          </div>
          <div>
            <Label htmlFor={`tip-${index}-bold`}>Text to Highlight (optional)</Label>
            <Input
              id={`tip-${index}-bold`}
              value={tip.boldText || ''}
              onChange={(e) => onUpdate(index, 'boldText', e.target.value)}
              placeholder="Text to highlight in bold..."
              maxLength={200}
            />
          </div>
        </div>

        {/* Remove button */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          className="mt-2"
          aria-label="Remove tip"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
